/**
 * GraphQL utility functions for optimized product fetching
 * Provides consolidated queries to reduce API calls and improve performance
 */

/**
 * Consolidated GraphQL query to fetch all product details with pagination
 * @param {string[]} styleNumbers - Array of style numbers to fetch
 * @param {string} shopUrl - Shopify shop URL
 * @param {AbortController} abortController - Abort controller for request cancellation
 * @returns {Promise<Object>} Products grouped by style number
 */
const fetchAllProductDetails = async (styleNumbers, shopUrl, abortController) => {
    const storefrontAccessToken = window.storefrontAccessToken;
    const url = `https://${shopUrl}/api/2024-10/graphql.json`;
    if (!styleNumbers || styleNumbers.length === 0) {
        console.warn('No style numbers provided for consolidated fetch');
        return {};
    }
        
    // Create a query that searches for multiple style numbers
    const queryConditions = styleNumbers.map(styleNo => `tag:${styleNo}`).join(' OR ') + ' AND status:ACTIVE';
    
    const productQuery = `
        query Products($query: String!, $country: CountryCode, $first: Int!, $after: String) @inContext(country: $country) {
            products(first: $first, after: $after, query: $query) {
                pageInfo {
                    hasNextPage
                    endCursor
                }
                edges {
                    node {
                        id
                        title
                        handle
                        tags
                        discontinued: metafield(namespace: "custom", key: "discontinued") {
                            value
                            type
                        }
                        merchReportingStyleNumber: metafield(namespace: "custom", key: "merch_reporting_style_number") {
                            value
                            type
                        }
                        variantSwatchColor: metafield(namespace: "custom", key: "variant_swatch_color") {
                            value
                            type
                        }
                        totalInventory
                        priceRange {
                            maxVariantPrice {
                                amount
                                currencyCode
                            }
                            minVariantPrice {
                                amount
                                currencyCode
                            }
                        }
                        images(first: 2) {
                            edges {
                                node {
                                    src
                                    altText
                                }
                            }
                        }
                        variants(first: 100) {
                            edges {
                                node {
                                    id
                                    title
                                    quantityAvailable
                                    price {
                                        amount
                                        currencyCode
                                    }
                                    compareAtPrice {
                                        amount
                                        currencyCode
                                    }
                                    selectedOptions {
                                        name
                                        value
                                    }
                                    image {
                                        src
                                        altText
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    `;

    try {
        let allProducts = [];
        let hasNextPage = true;
        let endCursor = null;
        let pageCount = 0;
        const maxPages = 10; // Safety limit to prevent infinite loops

        // Use while loop to fetch all pages
        while (hasNextPage && pageCount < maxPages) {
            if (abortController?.signal?.aborted) {
                console.log('Request aborted during pagination');
                break;
            }            
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    query: productQuery,
                    variables: {
                        query: queryConditions,
                        country: Shopify.country,
                        first: 250,
                        after: endCursor
                    }
                }),
                signal: abortController?.signal
            });

            const { data, errors } = await response.json();

            if (errors) {
                console.error('GraphQL Errors:', errors);
                break;
            }

            if (data && data?.products?.edges) {
                const pageProducts = data.products.edges
                    .map(({ node }) => {
                        if (node) {
                            return node;
                        } else {
                            console.warn("Encountered a null product.");
                            return null;
                        }
                    })
                    .filter(Boolean);

                allProducts = allProducts.concat(pageProducts);
                
                // Update pagination info
                hasNextPage = data.products.pageInfo.hasNextPage;
                endCursor = data.products.pageInfo.endCursor;
                pageCount++;
            } else {
                console.warn("No product data found on page", pageCount + 1);
                break;
            }
        }

        if (pageCount >= maxPages) {
            console.warn(`Reached maximum page limit (${maxPages}). There may be more products available.`);
        }

        // Group products by their style number for easy lookup
        const productsByStyle = {};
        allProducts.forEach(product => {
            let styleNumber = product.merchReportingStyleNumber?.value;

            if (!styleNumber) {
                styleNumber = (product.tags || []).find((tag) => styleNumbers.includes(tag));
            }

            if (styleNumber) {
                if (!productsByStyle[styleNumber]) {
                    productsByStyle[styleNumber] = [];
                }
                productsByStyle[styleNumber].push(product);
            }
        });
        return productsByStyle;

    } catch (error) {
        console.error("Error fetching consolidated product details:", error);
        return {};
    }
};

/**
 * Process custom swatches with consolidated data fetching and pagination
 * @param {Function} processProductDetails - Function to process individual product details
 * @param {AbortController} abortController - Abort controller for request cancellation
 * @param {string} shopUrl - Shopify shop URL
 * @returns {Promise<void>}
 */
const processCustomSwatchesOptimized = async (processProductDetails, abortController, shopUrl) => {
    sessionStorage.removeItem('natoriCustomVariants');
    const productCards = document.querySelectorAll(".product-card-swatch");

    // Collect all style numbers first
    const styleNumbers = [];
    const cardData = [];
    
    for (const card of productCards) {
        const productId = card.getAttribute('data-product-id');
        const productStyleNo = card.getAttribute('data-style-no');
        const productName = card.getAttribute('data-product-name');
        
        if (productStyleNo && !styleNumbers.includes(productStyleNo)) {
            styleNumbers.push(productStyleNo);
        }
        
        cardData.push({
            card,
            productId,
            productStyleNo,
            productName
        });
    }
    
    // Fetch all product details with pagination support
    let allProductDetails = {};
    if (styleNumbers.length > 0) {
        try {
            console.log(`Starting consolidated fetch for ${styleNumbers.length} style numbers with pagination support`);
            allProductDetails = await fetchAllProductDetails(styleNumbers, shopUrl, abortController);
        } catch (error) {
            console.error('Error fetching consolidated product details:', error);
            return;
        }
    }

    // Process each product card with the consolidated data
    for (const { card, productId, productStyleNo, productName } of cardData) {
        const prodOldSwatches = card.querySelector('.product-card-swatches');
        if (prodOldSwatches) {
            prodOldSwatches.innerHTML = '';
        }

        const color_settings = window['settings'];
        const settings = window[`settings_${productId}`];

        const __colorLookup = (window.NatoriColorSwatches && window.NatoriColorSwatches.getColorSwatchLookup)
            ? window.NatoriColorSwatches.getColorSwatchLookup()
            : (() => {
                const raw = (color_settings && color_settings.color_swatches) || '';
                const customColorsArray = raw.split(',').map((color) => {
                    const idx = color.indexOf(':');
                    if (idx === -1) return { key: color.trim().toLowerCase(), value: '' };
                    return { key: color.slice(0, idx).trim().toLowerCase(), value: color.slice(idx + 1).trim() || '' };
                });
                return {
                    keyArray: customColorsArray.map((color) => color.key),
                    valArray: customColorsArray.map((color) => color.value),
                };
            })();
            const keyArray = __colorLookup.keyArray;
            const valArray = __colorLookup.valArray;

        // Get product details from consolidated data
        const productDetails = allProductDetails[productStyleNo] || [];

        let uniqueColors = new Map();
        let counter = { count: 0 };

        const secondaryImageSrc = productDetails.filter(pd => pd.id === `gid://shopify/Product/${productId}`).flatMap(pd => pd.images.edges).map(edge => edge.node.src)[1];
        const prodCard = document.querySelector(`product-card.product-card:has(.product-card-swatch[data-product-id='${productId}'])`);
        const prodImage = prodCard.querySelector('figure.product-featured-image img.product-primary-image');
        const featuredImage = prodCard.querySelector('figure.product-featured-image');
        const prodImageContainer = prodCard.querySelector('figure.product-featured-image .product-featured-image-link');
        
        if(secondaryImageSrc){
            const prodSecondaryImage = document.createElement('img');
            prodSecondaryImage.src = secondaryImageSrc;
            prodSecondaryImage.alt = prodImage.alt ?? productName;
            prodSecondaryImage.style.width = '100%';
            prodSecondaryImage.style.height = '100%';
            prodSecondaryImage.classList.add('product-secondary-image');
            prodImageContainer.insertBefore(prodSecondaryImage, prodImage);
            featuredImage.classList.add('thb-hover');
        }

        const minPrice = Math.min(...productDetails.filter(pd => pd.totalInventory > 0).map(pd => pd.priceRange.minVariantPrice.amount));
        const maxPrice = Math.max(...productDetails.filter(pd => pd.totalInventory > 0).map(pd => pd.priceRange.maxVariantPrice.amount));
        const compareAtPrice = Math.min(...productDetails.filter(pd => pd.totalInventory > 0).flatMap(pd => pd.variants.edges.map(edge => edge.node?.compareAtPrice?.amount).filter(amount => amount != null))) || undefined;

        const prodPrices = prodCard.querySelector('.product-card-info .productInfoRight span.price');
        const priceContainer = minPrice !== maxPrice
            ? `<ins><span class="amount">${moneyFormat}${formatPrice(minPrice)} - ${moneyFormat}${formatPrice(maxPrice)}</span></ins>`
            : `<ins><span class="amount">${moneyFormat}${formatPrice(maxPrice)}</span></ins>`;
        const comparePriceContainer = compareAtPrice > minPrice ? `<del><span class="amount">${moneyFormat}${formatPrice(compareAtPrice)}</span></del>` : '';
        if (isFinite(minPrice) && isFinite(maxPrice) && (compareAtPrice === undefined || isFinite(compareAtPrice))) {
            prodPrices.innerHTML = comparePriceContainer + priceContainer;
        }
        prodCard.classList.remove('hide-price');

        for (const productDetail of productDetails) {
            if (abortController.signal.aborted) return;

            const productAvailable = productDetail.totalInventory > 0;
            const discontinued = productDetail?.discontinued?.value === "true";

            if (productAvailable && !discontinued) {
                const images = productDetail.images.edges.map(edge => edge.node);
                const primaryImagesrc = images[0]?.src || '';
                const secondaryImagesrc = images[1]?.src || '';

                await processProductDetails(
                    productDetail,
                    keyArray,
                    valArray,
                    settings.featured_image_id,
                    document.getElementById("swatch_section_" + productId),
                    productId,
                    primaryImagesrc,
                    secondaryImagesrc,
                    settings.custom_handle,
                    uniqueColors,
                    counter,
                    productAvailable
                );
                if (counter.count >= 6) break;
            }
        }

        const swatchSection = document.getElementById("swatch_section_" + productId);
        if (swatchSection) {
            const productCardParent = swatchSection.closest('.product-card');
            if (productCardParent && typeof productCardParent.enableSwatches === 'function') {
                productCardParent.enableSwatches();
            }
        }
    }

    try {
        await handleProductCardHover();
        await collectionSwatchImages();
    } catch (error) {
        console.error('Error handling swatch images or hover events:', error);
    }
};

const MARKET_CURRENCY_LOCALES = {
    GBP: 'en-GB',
    EUR: 'de-DE',
    USD: 'en-US',
    CAD: 'en-CA',
    AUD: 'en-AU',
    CHF: 'de-CH',
    JPY: 'ja-JP',
};

const MARKET_CURRENCY_SYMBOLS = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    CAD: '$',
    AUD: '$',
    CHF: 'CHF',
    JPY: '¥',
};

const normalizeMarketPriceToCents = (raw) => {
    if (raw == null) {
        return null;
    }

    if (typeof raw === 'number') {
        return Math.round(raw * 100);
    }

    if (typeof raw === 'string') {
        const str = raw.trim().replace(/[^0-9,.-]/g, '').replace(',', '.');
        if (!str) {
            return null;
        }
        const value = parseFloat(str);
        return Number.isFinite(value) ? Math.round(value * 100) : null;
    }

    return null;
};

const getMarketCurrencyCode = () => {
    if (typeof Shopify !== 'undefined' && Shopify.currency?.active) {
        return Shopify.currency.active;
    }

    return window.theme?.settings?.currency_code || 'USD';
};

const getMarketCurrencySymbol = (currencyCode) => {
    const code = currencyCode || getMarketCurrencyCode();

    if (MARKET_CURRENCY_SYMBOLS[code]) {
        return MARKET_CURRENCY_SYMBOLS[code];
    }

    if (
        window.theme?.settings?.currency_code === code &&
        window.theme?.settings?.currency_symbol
    ) {
        return window.theme.settings.currency_symbol;
    }

    const moneyFormat =
        window.theme?.settings?.money_format ||
        window.theme?.settings?.money_with_currency_format;
    if (typeof moneyFormat === 'string') {
        const symbolMatch = moneyFormat.match(/[\$£€¥₹]|CHF|R\$/);
        if (symbolMatch) {
            return symbolMatch[0];
        }
    }

    return '$';
};

const getMarketMoneyFormatForCurrency = (currencyCode) => {
    const code = currencyCode || getMarketCurrencyCode();

    if (
        typeof Shopify !== 'undefined' &&
        Shopify.currency?.active === code &&
        Shopify.money_format
    ) {
        return Shopify.money_format;
    }

    return `${getMarketCurrencySymbol(code)}{{amount}}`;
};

const getMarketMoneyFormat = () => getMarketMoneyFormatForCurrency(getMarketCurrencyCode());

const syncThemeCurrencySettings = (currencyCode) => {
    if (!currencyCode || !window.theme?.settings) {
        return;
    }

    const settings = window.theme.settings;
    settings.currency_code = currencyCode;
    settings.currency_symbol = getMarketCurrencySymbol(currencyCode);
    settings.money_format = getMarketMoneyFormatForCurrency(currencyCode);
    settings.money_with_currency_format = settings.money_format;
};

const getCurrencyCodeFromProduct = (product) =>
    product?.priceRange?.minVariantPrice?.currencyCode ||
    product?.priceRange?.maxVariantPrice?.currencyCode ||
    product?.variants?.edges?.find((edge) => edge?.node?.price?.currencyCode)?.node?.price
        ?.currencyCode ||
    null;

const getCurrencyCodeFromProducts = (products) => {
    for (const product of products || []) {
        const code = getCurrencyCodeFromProduct(product);
        if (code) {
            return code;
        }
    }

    return getMarketCurrencyCode();
};

const updateProductCardCurrencyMetadata = (productCard, currencyCode) => {
    if (!productCard || !currencyCode) {
        return;
    }

    syncThemeCurrencySettings(currencyCode);
    productCard.dataset.currencyCode = currencyCode;

    const priceRoot = productCard.querySelector('.productInfoRight .price, .product-card-info .price');
    if (!priceRoot) {
        return;
    }

    priceRoot.dataset.currencyCode = currencyCode;

    const priceCurrency = priceRoot.querySelector('.price-currency');
    if (priceCurrency) {
        priceCurrency.textContent = JSON.stringify(getMarketMoneyFormatForCurrency(currencyCode));
    }
};

const formatMarketPriceFromRaw = (raw, currencyCode) => {
    const activeCurrency = currencyCode || getMarketCurrencyCode();
    const cents = normalizeMarketPriceToCents(raw);
    if (cents == null) {
        return '';
    }

    try {
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
            return Shopify.formatMoney(cents, getMarketMoneyFormatForCurrency(activeCurrency));
        }
    } catch (error) {
        console.error('formatMarketPriceFromRaw formatMoney error', error);
    }

    const amount = cents / 100;

    try {
        const locale = MARKET_CURRENCY_LOCALES[activeCurrency] || 'en-US';
        let formatted = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: activeCurrency,
            maximumFractionDigits: 2,
        }).format(amount);
        return formatted.replace(/\.00(?=\D*$)/, '');
    } catch (error) {
        return `${getMarketCurrencySymbol(activeCurrency)}${amount.toFixed(2)}`;
    }
};

const getInStockProductsForPricing = (products) =>
    (products || []).filter((product) => {
        const inventory = product?.totalInventory;
        return inventory == null || inventory > 0;
    });

const getStylePriceBounds = (products) => {
    const inStock = getInStockProductsForPricing(products);
    if (!inStock.length) {
        return null;
    }

    const mins = inStock
        .map((product) => parseFloat(product.priceRange?.minVariantPrice?.amount))
        .filter((amount) => Number.isFinite(amount));
    const maxs = inStock
        .map((product) => parseFloat(product.priceRange?.maxVariantPrice?.amount))
        .filter((amount) => Number.isFinite(amount));

    if (!mins.length || !maxs.length) {
        return null;
    }

    return {
        min: Math.min(...mins),
        max: Math.max(...maxs),
    };
};

const getStyleCompareAt = (products, saleMin) => {
    const amounts = getInStockProductsForPricing(products)
        .flatMap((product) =>
            (product?.variants?.edges || [])
                .map((edge) => edge?.node?.compareAtPrice?.amount)
                .filter(Boolean)
        )
        .map((amount) => parseFloat(amount))
        .filter((amount) => Number.isFinite(amount));

    if (!amounts.length || saleMin == null) {
        return undefined;
    }

    const compareAt = Math.min(...amounts);
    return compareAt > saleMin ? compareAt : undefined;
};

const applyMarketPriceMarkup = (priceRoot, min, max, compareAt, currencyCode) => {
    if (!priceRoot || min == null || max == null) {
        return;
    }

    const isPriceRange = min !== max;
    const activeCurrency = currencyCode || priceRoot.dataset.currencyCode || getMarketCurrencyCode();
    const displayMin = formatMarketPriceFromRaw(min, activeCurrency);
    const displayMax = formatMarketPriceFromRaw(max, activeCurrency);
    const saleText =
        isPriceRange && displayMin && displayMax
            ? `${displayMin} – ${displayMax}`
            : displayMax || displayMin;

    const priceCents = normalizeMarketPriceToCents(min);
    const compareCents =
        compareAt != null ? normalizeMarketPriceToCents(compareAt) : null;
    const showCompare =
        !isPriceRange &&
        compareCents != null &&
        priceCents != null &&
        compareCents > priceCents;

    priceRoot.dataset.currencyCode = activeCurrency;
    priceRoot.classList.toggle('has-price-range', isPriceRange);

    let delEl = priceRoot.querySelector('del');
    let insEl = priceRoot.querySelector('ins');

    if (!insEl) {
        insEl = document.createElement('ins');
        insEl.innerHTML = '<span class="amount"></span>';
        priceRoot.appendChild(insEl);
    }

    const amountEl = insEl.querySelector('.amount') || insEl;
    amountEl.textContent = saleText;

    if (showCompare) {
        const compareFormatted = formatMarketPriceFromRaw(compareAt, activeCurrency);
        if (!delEl) {
            delEl = document.createElement('del');
            delEl.innerHTML = '<span class="amount"></span>';
            priceRoot.insertBefore(delEl, insEl);
        }
        const delAmount = delEl.querySelector('.amount') || delEl;
        delAmount.textContent = compareFormatted;
    } else if (delEl) {
        delEl.remove();
    }
};

const getProductHandleFromUrl = (url) => {
    if (!url) {
        return '';
    }

    try {
        const path = String(url).startsWith('http')
            ? new URL(url).pathname
            : String(url).split('?')[0];
        const match = path.match(/\/products\/([^/?#]+)/i);
        return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
        return '';
    }
};

const getProductSalePrice = (product) => {
    const amount = product?.priceRange?.minVariantPrice?.amount;
    const parsed = parseFloat(amount);
    return Number.isFinite(parsed) ? parsed : null;
};

const getProductCompareAt = (product, salePrice) => {
    const amounts = (product?.variants?.edges || [])
        .map((edge) => edge?.node?.compareAtPrice?.amount)
        .filter(Boolean)
        .map((amount) => parseFloat(amount))
        .filter((amount) => Number.isFinite(amount));

    if (!amounts.length || salePrice == null) {
        return undefined;
    }

    const compareAt = Math.min(...amounts);
    return compareAt > salePrice ? compareAt : undefined;
};

const hydrateProductCardSwatchPrices = (productCard, products, currencyCode) => {
    if (!productCard || !products?.length) {
        return;
    }

    const activeCurrency = currencyCode || getCurrencyCodeFromProducts(products);
    const productsByHandle = new Map();
    products.forEach((product) => {
        if (product?.handle) {
            productsByHandle.set(product.handle, product);
        }
    });

    productCard.querySelectorAll('.product-card-swatch[data-href]').forEach((swatch) => {
        const handle = getProductHandleFromUrl(swatch.getAttribute('data-href'));
        const swatchProduct = productsByHandle.get(handle);
        if (!swatchProduct) {
            return;
        }

        const salePrice = getProductSalePrice(swatchProduct);
        if (salePrice == null) {
            return;
        }

        const swatchCurrencyCode =
            getCurrencyCodeFromProduct(swatchProduct) || activeCurrency;
        const swatchCompareAt = getProductCompareAt(swatchProduct, salePrice);
        swatch.setAttribute('data-price', String(salePrice));
        swatch.setAttribute(
            'data-compare-at-price',
            swatchCompareAt != null ? String(swatchCompareAt) : ''
        );
        swatch.setAttribute('data-currency-code', swatchCurrencyCode);
    });
};

const hydrateProductCardMarketPrice = (productCard, products) => {
    if (!productCard || !products?.length) {
        return false;
    }

    const currencyCode = getCurrencyCodeFromProducts(products);
    updateProductCardCurrencyMetadata(productCard, currencyCode);

    const bounds = getStylePriceBounds(products);
    if (!bounds) {
        productCard.classList.remove('hide-price');
        hydrateProductCardSwatchPrices(productCard, products, currencyCode);
        return false;
    }

    const compareAt = getStyleCompareAt(products, bounds.min);
    const priceRoot = productCard.querySelector('.productInfoRight .price, .product-card-info .price');
    applyMarketPriceMarkup(priceRoot, bounds.min, bounds.max, compareAt, currencyCode);
    productCard.dataset.defaultPrice = String(bounds.min);
    productCard.dataset.defaultCompareAtPrice =
        compareAt != null ? String(compareAt) : '';
    hydrateProductCardSwatchPrices(productCard, products, currencyCode);
    productCard.classList.remove('hide-price');

    if (typeof productCard.enableSwatches === 'function') {
        productCard.enableSwatches();
    }

    return true;
};

const fetchProductsByIds = async (productIds, abortController) => {
    if (!productIds?.length || typeof Shopify === 'undefined') {
        return [];
    }

    const storefrontAccessToken =
        window.storefrontAccessToken ||
        (typeof window.waitForStorefrontToken === 'function'
            ? await window.waitForStorefrontToken(2500).catch(() => null)
            : null);

    if (!storefrontAccessToken) {
        return [];
    }

    const shopUrl = Shopify.shop;
    const url = `https://${shopUrl}/api/2024-10/graphql.json`;
    const gids = productIds.map((id) =>
        String(id).startsWith('gid://') ? id : `gid://shopify/Product/${id}`
    );
    const chunkSize = 50;
    const allProducts = [];

    for (let index = 0; index < gids.length; index += chunkSize) {
        const ids = gids.slice(index, index + chunkSize);
        const query = `
            query ProductsByIds($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) {
                nodes(ids: $ids) {
                    ... on Product {
                        id
                        handle
                        title
                        totalInventory
                        priceRange {
                            minVariantPrice { amount currencyCode }
                            maxVariantPrice { amount currencyCode }
                        }
                        variants(first: 50) {
                            edges {
                                node {
                                    compareAtPrice { amount currencyCode }
                                    price { amount currencyCode }
                                }
                            }
                        }
                    }
                }
            }
        `;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
            },
            body: JSON.stringify({
                query,
                variables: {
                    ids,
                    country: Shopify.country,
                },
            }),
            signal: abortController?.signal,
        });

        const payload = await response.json();
        if (payload?.errors) {
            console.error('fetchProductsByIds GraphQL errors:', payload.errors);
            continue;
        }

        allProducts.push(...(payload?.data?.nodes || []).filter(Boolean));
    }

    return allProducts;
};

const hydrateRecommendationMarketPrices = async (root = document) => {
    const cards = [
        ...root.querySelectorAll(
            'product-recommendations product-card, .product-recommendations-drawer product-card'
        ),
    ];

    if (!cards.length) {
        return;
    }

    const styleNumbers = [];
    const orphanProductIds = [];

    cards.forEach((card) => {
        const swatch = card.querySelector('.product-card-swatch[data-product-id]');
        const styleNo = swatch?.getAttribute('data-style-no')?.trim();
        const productId = swatch?.getAttribute('data-product-id');

        if (styleNo && !styleNumbers.includes(styleNo)) {
            styleNumbers.push(styleNo);
        } else if (productId && !orphanProductIds.includes(productId)) {
            orphanProductIds.push(productId);
        }
    });

    let productsByStyle = {};
    if (styleNumbers.length) {
        try {
            productsByStyle = await fetchAllProductDetails(styleNumbers, Shopify.shop);
        } catch (error) {
            console.error('Recommendation style price hydration failed', error);
        }
    }

    let orphanProducts = [];
    if (orphanProductIds.length) {
        try {
            orphanProducts = await fetchProductsByIds(orphanProductIds);
        } catch (error) {
            console.error('Recommendation product price hydration failed', error);
        }
    }

    const productsById = new Map();
    orphanProducts.forEach((product) => {
        if (product?.id) {
            productsById.set(String(product.id).replace('gid://shopify/Product/', ''), product);
        }
    });

    cards.forEach((card) => {
        const swatch = card.querySelector('.product-card-swatch[data-product-id]');
        const styleNo = swatch?.getAttribute('data-style-no')?.trim();
        const productId = swatch?.getAttribute('data-product-id');
        const styleProducts = styleNo ? productsByStyle[styleNo] || [] : [];
        const productsForCard =
            styleProducts.length > 0
                ? styleProducts
                : productsById.get(String(productId || ''))
                  ? [productsById.get(String(productId || ''))]
                  : [];

        hydrateProductCardMarketPrice(card, productsForCard);
    });
};

// Export functions for use in other files
if (typeof module !== 'undefined' && module.exports) {
    // Node.js environment
    module.exports = {
        fetchAllProductDetails,
        processCustomSwatchesOptimized,
        formatMarketPriceFromRaw,
        applyMarketPriceMarkup,
        getMarketCurrencyCode,
        getMarketCurrencySymbol,
        getMarketMoneyFormatForCurrency,
        syncThemeCurrencySettings,
        updateProductCardCurrencyMetadata,
        hydrateProductCardMarketPrice,
        hydrateProductCardSwatchPrices,
        hydrateRecommendationMarketPrices,
        fetchProductsByIds,
    };
} else {
    // Browser environment
    window.GraphQLUtils = {
        fetchAllProductDetails,
        processCustomSwatchesOptimized,
        formatMarketPriceFromRaw,
        applyMarketPriceMarkup,
        getMarketCurrencyCode,
        getMarketCurrencySymbol,
        getMarketMoneyFormatForCurrency,
        syncThemeCurrencySettings,
        updateProductCardCurrencyMetadata,
        hydrateProductCardMarketPrice,
        hydrateProductCardSwatchPrices,
        hydrateRecommendationMarketPrices,
        fetchProductsByIds,
    };

    window.formatMarketPriceFromRaw = formatMarketPriceFromRaw;
    window.getMarketCurrencyCode = getMarketCurrencyCode;

    if (!window.teqFormatPriceFromRaw) {
        window.teqFormatPriceFromRaw = formatMarketPriceFromRaw;
    }
}
